import { makeAutoObservable, runInAction } from "mobx";
import OnboardingDataStore, { IOnboardingScreenStore } from "./onboarding-data-store";
import onboardingPlansStore from "./onboarding-plans-stores";

import NewUserWelcomeScreen from '../components/welcome-screen/new-user-welcome-screen';
import { ONBOARDING_STEPS, PLAN_TYPE } from "../constants";
import { IStepComponent } from "../onboarding-page";
import ChoosePlanTypeScreen from "../components/choose-plan-type-screen/choose-plan-type-screen";
import ChoosePlanScreen from "../components/choose-plan-screen/choose-plan-screen";
import ChooseResolutionScreen from "../components/choose-resolution-screen/choose-resolution-screen";
import ApprovePlanScreen from "../components/approve-plan-onboarding-screen/approve-plan-onboarding-screen";
import FinalOnboardingScreen from "../components/final-onboarding-screen/final-onboarding-screen";
import { rootContainer, TYPE } from "../../../services/container/root";
import EnableReturnGuardOnboardingScreen from "../components/enable-return-guard-onboarding-screen/enable-return-guard-onboarding-screen";
import AddReturnGuardToThemeScreen from "../components/add-return-guard-to-theme-screen/add-return-guard-to-theme-screen";
import CreateReturnMethodOnboardingScreen from "../components/create-return-method-onboarding-screen/create-return-method-onboarding-screen";
import { IIframeService } from "../../../services/iframe-service/iframe-service";
import { isSet } from "../../../models/model-types-methods";
import onboardingMixPanelStore, {ONBOARDING_MIXPANEL_EVENT} from "./onboarding-mixpanel-store";
import currentPortalStore from "../../../stores/current-portal-store";
import WalletConfigurationOnboardingScreen from "../components/wallet-configuration-onboarding-screen/wallet-configuration-onboarding-screen";
import walletStore from "../../billing/stores/wallet/wallet-store";

const COMPONENTS_MAP: Record<ONBOARDING_STEPS, React.FC<IStepComponent>> = {
    [ONBOARDING_STEPS.NEW_USER_WELCOME]: NewUserWelcomeScreen,
    [ONBOARDING_STEPS.CHOOSE_PLAN_TYPE]: ChoosePlanTypeScreen,
    [ONBOARDING_STEPS.CHOOSE_PLAN]: ChoosePlanScreen,
    [ONBOARDING_STEPS.CHOOSE_RETURN_TYPE]: ChooseResolutionScreen,
    [ONBOARDING_STEPS.CREATE_RETURN_METHOD]: CreateReturnMethodOnboardingScreen,
    [ONBOARDING_STEPS.WALLET_CONFIGURATION]: WalletConfigurationOnboardingScreen,
    [ONBOARDING_STEPS.ENABLE_RETURN_GUARD]: EnableReturnGuardOnboardingScreen,
    [ONBOARDING_STEPS.SETUP_RETURN_GUARD]: AddReturnGuardToThemeScreen,
    [ONBOARDING_STEPS.APPROVE_PLAN]: ApprovePlanScreen,
    [ONBOARDING_STEPS.FINAL]: FinalOnboardingScreen,
}

const SCREENS_WITH_NO_STEPPER = [
    ONBOARDING_STEPS.NEW_USER_WELCOME,
    ONBOARDING_STEPS.APPROVE_PLAN,
    ONBOARDING_STEPS.FINAL
];

export interface IOnboardingStep {
    name: ONBOARDING_STEPS;
    component: React.FC<IStepComponent>;
}

export class OnboardingNavigationStore {
    private _isLoading: boolean = false;
    private _isLoaded: boolean = false;
    private _onboardingDataStore: OnboardingDataStore = new OnboardingDataStore(this);

    public get isLoading(): boolean { return this._isLoading; }
    public get isLoaded(): boolean { return this._isLoaded; }

    private _currentStep: ONBOARDING_STEPS = ONBOARDING_STEPS.NEW_USER_WELCOME;
    private readonly _onboardingSteps: Omit<IOnboardingStep, 'component'>[] = [
        {
            name: ONBOARDING_STEPS.NEW_USER_WELCOME,
        },
        {
            name: ONBOARDING_STEPS.CHOOSE_PLAN_TYPE,
        },
        {
            name: ONBOARDING_STEPS.CHOOSE_PLAN,
        },
        {
            name: ONBOARDING_STEPS.CHOOSE_RETURN_TYPE,
        },
        {
            name: ONBOARDING_STEPS.CREATE_RETURN_METHOD,
        },
        {
            name: ONBOARDING_STEPS.WALLET_CONFIGURATION,
        },
        {
            name: ONBOARDING_STEPS.ENABLE_RETURN_GUARD,
        },
        {
            name: ONBOARDING_STEPS.SETUP_RETURN_GUARD,
        },
        {
            name: ONBOARDING_STEPS.APPROVE_PLAN,
        },
        {
            name: ONBOARDING_STEPS.FINAL,
        },
    ];

    constructor() {
        makeAutoObservable(this, {}, { autoBind: true });
    }

    private get iframeService(): IIframeService {
        return rootContainer.get<IIframeService>(TYPE.IframService);
    }

    private get offeredOnboardingSteps() {
        const defaultCoveragePlanExists = Boolean(onboardingPlansStore.onboardingCoveragePlan);
        /** while plan type is not yet set - treat as True if there is a coverage plan*/
        const isCoveragePlanType =  isSet(this._onboardingDataStore.getPlanType())
            ? this._onboardingDataStore.getPlanType() === PLAN_TYPE.COVERAGE
            : defaultCoveragePlanExists;

        const res = this._onboardingSteps.filter(step => {
            /** skipping choose plan type screen when there are no coverage-enabled plans */
            if (!defaultCoveragePlanExists) {
                if (step.name === ONBOARDING_STEPS.CHOOSE_PLAN_TYPE) {
                    return false;
                }
            }

            /** skipping choose plan screen when selected Coverage plan Type */
            if (isCoveragePlanType) {
                if (step.name === ONBOARDING_STEPS.CHOOSE_PLAN) {
                    return false;
                }
            }

            /** when actual plan id is selected - count on it to filter out those screens */
            if (this._onboardingDataStore.isPlanIdSelected()) {
                /** skipping Coverage-extension screens for non Coverage plans */
                if (!this._onboardingDataStore.isSelectedCoveragePlanId() || this._onboardingDataStore.getSetting()?.should_hide_coverage_screens) {
                    if ([ONBOARDING_STEPS.ENABLE_RETURN_GUARD, ONBOARDING_STEPS.SETUP_RETURN_GUARD].includes(step.name)) {
                        return false;
                    }
                }
            }
            /** while the actual plan id is not selected - count on plan type to filter those screens out */
            else if (!isCoveragePlanType) {
                if ([ONBOARDING_STEPS.ENABLE_RETURN_GUARD, ONBOARDING_STEPS.SETUP_RETURN_GUARD].includes(step.name)) {
                    return false;
                }
            }

            if (!this._onboardingDataStore.getSetting()?.should_wizard_collect_resolutions) {
                if ([ONBOARDING_STEPS.CHOOSE_RETURN_TYPE].includes(step.name)) {
                    return false;
                }
            }

            if (!this._onboardingDataStore.getSetting()?.should_wizard_collect_return_method) {
                if ([ONBOARDING_STEPS.CREATE_RETURN_METHOD].includes(step.name)) {
                    return false;
                }
            }

            return true;
        });

        return res;
    }

    public getNavigationComponentProps(): { list: boolean[], is_hidden: boolean } {
        const progressSteps = this.offeredOnboardingSteps.filter(step => !SCREENS_WITH_NO_STEPPER.includes(step.name)).map(step => {
            return step.name === this._currentStep;
        });

        return {
            list: progressSteps,
            is_hidden: !progressSteps.some(s => s), /** when all steps are False - hide the stepper */
        };
    }

    private _setInitialCurrentStep(): void {
        const firstIncompletedStep = this.offeredOnboardingSteps.find((step) => !this.isStepComplete(step.name));
        if (!firstIncompletedStep) {
            /** when all the screens are complete - set current step to first one */
            this._currentStep = this.offeredOnboardingSteps[0].name;
            return;
        }

        const firstIncompletedStepIndex = this.offeredOnboardingSteps.indexOf(firstIncompletedStep);
        this._currentStep = this.offeredOnboardingSteps[firstIncompletedStepIndex].name;

        onboardingMixPanelStore.trackEvent(this._onboardingDataStore.getModel()?.id ?
                ONBOARDING_MIXPANEL_EVENT.ONBOARDING_CONTINUED :
                ONBOARDING_MIXPANEL_EVENT.ONBOARDING_STARTED,
                {
                    from: this._currentStep
                }
            );
    }

    public previousStep(): void {
        const stepIndex = this.offeredOnboardingSteps.findIndex(step => step.name === this._currentStep);
        if (stepIndex <= 0) {
            console.error('[onboarding wizard] should never happen, can not go to previous step, we are on the first step or previous step was not found');
            return;
        }
        const fromStep = this._currentStep; // save the step where we are now
        this._currentStep = this.offeredOnboardingSteps[stepIndex - 1].name;
        onboardingMixPanelStore.trackEvent(ONBOARDING_MIXPANEL_EVENT.ONBOARDING_PREV_STEP, {
            from: fromStep,
            to: this._currentStep
        });
    }

    public nextStep(): void {
        const stepIndex = this.offeredOnboardingSteps.findIndex(step => step.name === this._currentStep);
        const nextStepIndex = stepIndex + 1;
        if (!this.offeredOnboardingSteps[nextStepIndex]) {
            console.error('[onboarding wizard] should never happen - attemp to move next beyound last screen');
            return;
        }

        /** called from screen stores after async method save - hence run in action */
        runInAction(() => {
            const fromStep = this._currentStep; // save the step where we are now
            this._currentStep = this.offeredOnboardingSteps[nextStepIndex].name;
            onboardingMixPanelStore.trackEvent(ONBOARDING_MIXPANEL_EVENT.ONBOARDING_NEXT_STEP, {
                from: fromStep,
                to: this._currentStep
            });
        });
    }

    private isStepComplete(stepName: ONBOARDING_STEPS): boolean {
        const screenStore = this._onboardingDataStore.getScreenStore(stepName);
        const isComplete = screenStore.isComplete();
        return isComplete;
    }

    public getCurrentStep(): IOnboardingStep {
        const step = this.offeredOnboardingSteps.find(step => step.name === this._currentStep);
        if (!step) {
            const onboarding = this._onboardingDataStore.getModel();
            const info = [onboarding?.id, this._onboardingDataStore.getPlanType()].filter(Boolean).join('|');
            throw  new Error(`Should not happen, could not find onboarding step ${this._currentStep}-id:${info}`);
        }
        return { ...step, component: COMPONENTS_MAP[step.name]};
    }

    public getScreenStore(stepName: ONBOARDING_STEPS): IOnboardingScreenStore | undefined {
        return this._onboardingDataStore.getScreenStore(stepName);
    }

    public async load() {
        this._isLoading = true;

        await Promise.all([
            this._onboardingDataStore.load(),
            currentPortalStore.load(),
            onboardingPlansStore.load(),
            walletStore.load(),
        ]);

        runInAction(() => {
            this._isLoading = false;
            this._isLoaded = true;
            this._setInitialCurrentStep();
        });
	}

    public openSupportChat() {
        onboardingMixPanelStore.trackEvent(ONBOARDING_MIXPANEL_EVENT.ONBOARDING_CUSTOMER_SUPPORT_LINK_CLICKED, {
            from: this._currentStep
        });
        this.iframeService.openParentIframeChat();
    }

}

const onboardingNavigationStore = new OnboardingNavigationStore();
export default onboardingNavigationStore;
